import React, { memo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  ChevronDown, 
  ChevronUp, 
  Clock, 
  Calendar, 
  AlertTriangle
} from 'lucide-react';
import { CalendarAlert } from '@/utils/api';

interface CalendarAlertCardProps {
  alert: CalendarAlert;
  isExpanded: boolean;
  toggleExpand: (id: number) => void;
}

// Utility functions for volatility colors
const getVolatilityColor = (intensity: string) => {
  switch (intensity.toLowerCase()) {
    case 'critical':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'high':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'medium':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'low':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    default:
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  }
};

// Format date with timezone
const formatDateWithTimezone = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const formattedDate = format(date, 'MMM d, yyyy h:mm a');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${formattedDate} (${timezone})`;
  } catch (error) {
    return 'Invalid date';
  }
};

const CalendarAlertCard = memo(({ alert, isExpanded, toggleExpand }: CalendarAlertCardProps) => {
  const intensityClass = getVolatilityColor(alert.window_volatility_intensity);
  
  // Function to safely render HTML content
  const createMarkup = (content: string) => {
    return { __html: content };
  };

  // Format the full analysis text to improve readability with proper structure
  const formatAnalysisText = (text: string | undefined | null) => {
    if (!text) return '';
    
    // Clean up any JSON artifacts (escaped quotes, escaped newlines, etc.)
    let cleanedText = text
      .replace(/^\s*["'{]|["'}]\s*$/g, '')  // Remove surrounding quotes/braces
      .replace(/\\n/g, '\n')                // Replace escaped newlines
      .replace(/\\t/g, '')                  // Remove tabs
      .replace(/\\"/g, '"')                 // Replace escaped quotes
      .replace(/\\\\|\\'|'/g, '');           // Remove escaped backslashes

    // Remove extra quotation marks at the beginning of paragraphs and end of content
    cleanedText = cleanedText.replace(/"([^"]+)/g, '$1');
    cleanedText = cleanedText.replace(/([^"]+)"/g, '$1');
    
    // Remove trailing commas and periods at the end of paragraphs and sections
    // This handles both HTML tag boundaries and entire text boundaries
    cleanedText = cleanedText.replace(/,\s*(?=<\/span>|<\/div>|<\/p>|$|\n|\r)/g, '');
    
    // Also look for trailing commas in the raw text before any formatting is done
    cleanedText = cleanedText.replace(/(\w+),\s*(?=[A-Z][A-Z\s]+:|$)/g, '$1');
    
    // Remove any trailing commas before section headers
    cleanedText = cleanedText.replace(/,\s+([A-Z][A-Z\s]+:)/g, ' $1');

    // Simple approach: look for section headers in all caps followed by colons
    // MARKET CONTEXT: etc.
    const sectionPattern = /([A-Z][A-Z\s]+):/g;
    
    // Replace section headers with styled HTML
    cleanedText = cleanedText.replace(sectionPattern, 
      '<div class="mt-4 mb-2"><span class="text-blue-400 font-semibold">$1:</span></div>');
    
    // Handle numbered lists - extract the entire list section
    cleanedText = cleanedText.replace(/ACTION STEPS:[\s\S]*?(\d+\.[\s\S]*?)(?=<div|$)/g, function(match, listContent) {
      let listHtml = '<div class="mt-4 mb-2"><span class="text-blue-400 font-semibold">ACTION STEPS:</span></div><div class="ml-4 space-y-2">';
      
      // Process each numbered item
      const listItems = listContent.split(/(?=\d+\.)/g);
      listItems.forEach(item => {
        if (!item.trim()) return;
        
        // Extract the number and content
        const parts = item.match(/(\d+\.)(.*)/s);
        if (parts) {
          const [_, number, content] = parts;
          listHtml += `<div class="flex mb-2"><span class="text-blue-400 font-medium mr-2 w-7 flex-shrink-0">${number}</span><span class="flex-1">${content.trim()}</span></div>`;
        }
      });
      
      return listHtml + '</div>';
    });
    
    // Add paragraph breaks after periods that are followed by spaces for remaining content
    cleanedText = cleanedText.replace(/([^>])\.(\s+)(?![^<]*>)/g, '$1.</p><p class="mb-2">');
    
    // Make sure we have opening and closing paragraph tags
    cleanedText = cleanedText.replace(/<div/g, '</p><div');
    cleanedText = '<p class="mb-2">' + cleanedText;
    
    // Fix any double paragraph tags or empty paragraphs
    cleanedText = cleanedText.replace(/<\/p><\/p>/g, '</p>');
    cleanedText = cleanedText.replace(/<\/p><p class="mb-2"><\/p>/g, '</p>');
    cleanedText = cleanedText.replace(/<p class="mb-2"><\/p>/g, '');
    
    return cleanedText;
  };
  
  const formattedAnalysis = formatAnalysisText(alert.full_analysis);
  
  return (
    <Card 
      key={`calendar-${alert.id}`}
      className={cn(
        "overflow-hidden transition-all duration-300 border border-white/10",
        "bg-gradient-to-b from-sidebar/80 to-sidebar backdrop-blur-sm relative",
        "hover:shadow-md hover:shadow-blue-900/20 group"
      )}
    >
      <div className={`absolute top-0 left-0 w-1 h-full ${intensityClass}`}></div>
      <div 
        className="p-4 cursor-pointer" 
        onClick={() => toggleExpand(alert.id)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="p-2 rounded-full bg-red-500/10 text-red-400">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-white">Strykr Calendar Alert</h3>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  CALENDAR
                </Badge>
              </div>
              <div 
                className="mt-1 text-white/90 text-left"
                dangerouslySetInnerHTML={{ __html: alert.short_summary }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={intensityClass}>
              {alert.window_volatility_intensity} ({alert.strykr_score.toFixed(1)})
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
        <div className="flex items-center mt-3 text-sm text-white/60">
          <Clock className="h-3.5 w-3.5 mr-1.5" />
          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-white/10">
          <div className="bg-black/30 rounded-lg overflow-hidden">
            <div className="text-center py-4 border-b border-gray-800/50">
              <h4 className="font-medium text-white text-lg">Full Analysis</h4>
            </div>
            <div 
              className="text-white/90 text-sm max-w-none text-left p-6 bg-[#0b0e14]"
              style={{ lineHeight: '1.6', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: formattedAnalysis }}
            />
            
            <div className="mt-6 mb-4">
              <div className="flex items-center mb-2">
                <Clock className="h-4 w-4 mr-2 text-blue-400" />
                <h5 className="font-medium text-white/80 text-sm">Volatile Window</h5>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-black/40 rounded-lg p-3 border border-white/5 flex items-center">
                  <Calendar className="h-4 w-4 mr-3 text-blue-400" />
                  <div>
                    <div className="text-xs text-white/60 mb-1">Start</div>
                    <div className="text-sm text-white">{formatDateWithTimezone(alert.volatile_window_start)}</div>
                  </div>
                </div>
                <div className="bg-black/40 rounded-lg p-3 border border-white/5 flex items-center">
                  <Calendar className="h-4 w-4 mr-3 text-blue-400" />
                  <div>
                    <div className="text-xs text-white/60 mb-1">End</div>
                    <div className="text-sm text-white">{formatDateWithTimezone(alert.volatile_window_end)}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                <h5 className="font-medium text-white/80 text-sm">Strykr Score: {alert.strykr_score.toFixed(1)} - {alert.window_volatility_intensity} volatility expected (Rating: {alert.window_volatility_rating.toFixed(1)})</h5>
              </div>
            </div>
            
            {alert.events_analyzed && alert.events_analyzed.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center mb-3">
                  <Calendar className="h-4 w-4 mr-2 text-blue-400" />
                  <h5 className="font-medium text-white/80 text-sm">Events Analyzed:</h5>
                </div>
                
                <div className="bg-black/40 rounded-lg border border-white/5 overflow-hidden">
                  <div className="text-xs text-white/60 grid grid-cols-4 p-2 border-b border-white/10 bg-black/60">
                    <div className="col-span-1 pl-2">Event</div>
                    <div className="col-span-1 text-center">Country</div>
                    <div className="col-span-1 text-center">Currency</div>
                    <div className="col-span-1 text-center">Impact</div>
                  </div>
                  
                  {alert.events_analyzed.map((event, index) => (
                    <div 
                      key={index} 
                      className={`grid grid-cols-4 p-2 text-sm items-center ${index % 2 === 1 ? 'bg-black/20' : ''}`}
                    >
                      <div className="col-span-1 pl-2 text-white">{event.name}</div>
                      <div className="col-span-1 text-center">{event.country}</div>
                      <div className="col-span-1 text-center">{event.currency}</div>
                      <div className="col-span-1 flex justify-center">
                        <Badge 
                          className={cn(
                            "text-xs px-3",
                            event.impact === "High" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                            event.impact === "Medium" ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" :
                            "bg-green-500/20 text-green-400 border-green-500/30"
                          )}
                        >
                          {event.impact} Impact
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
});

CalendarAlertCard.displayName = 'CalendarAlertCard';

export default CalendarAlertCard;
